// cardsForRecipe — predict the card rewards a kid earns by eating
// a given dish. Maps each ingredient line to a card kind (build /
// super / boost / spark / dairy / water) based on the ingredient's
// category + subtype, then sums with line-level quantity.
//
// Used by the kid-mode preview chips on recipe pages and kitchen
// items so the kid sees BEFORE eating "this gives 3 builds + 1
// protect." Same prediction is what'll fire from the cook-log
// trigger once we wire the auto-grant path.

// One-shot heavy ingredients — each line counts toward the card
// regardless of small-amount thresholds. Map: category/subtype →
// card kind. The keys are matched against ingredient.category
// FIRST, then ingredient.subtype, so a row tagged
// `category: "dairy", subtype: "butter"` lands on dairy not on
// some butter-specific override.
const CATEGORY_TO_CARD = {
  // Build (protein)
  meat:    "build",
  poultry: "build",
  fish:    "build",
  seafood: "build",
  // Dairy (calcium / protect)
  dairy:   "dairy",
  // Super (veggies / greens)
  produce: "super",
  // Boost (grains / energy)
  grain:   "boost",
  bread:   "boost",
};

// Subtype overrides — finer grain than category. Note many of these
// are tier-3 assembled products (cookies, candy, etc.) that we want
// classified as "spark" (treat) instead of whatever broader category
// they fell into.
const SUBTYPE_TO_CARD = {
  // Treats — Spark category
  cookie:       "spark",
  candy:        "spark",
  candy_bar:    "spark",
  chocolate:    "spark",
  ice_cream:    "spark",
  dessert:      "spark",
  sweetener:    "spark",
  spice:        "spark",
  // Grains / bread variants → Boost
  bread:        "boost",
  pasta:        "boost",
  rice:         "boost",
  cereal:       "boost",
  // Dairy sub-variants stay dairy (explicit listing avoids subtype
  // overrides accidentally re-routing them).
  cheese:       "dairy",
  yogurt:       "dairy",
  butter:       "dairy",
  sour_cream:   "dairy",
  // Protein subtypes — kept as build (some live under category=meat
  // already; redundant here is safe).
  chicken_cut:  "build",
  deli:         "build",
  sausage:      "build",
  nut:          "build",
  // Beverage — water if explicitly water/juice. Tea/coffee/soda left
  // unset (no reward).
  beverage:     null,
};

// Hub-level fallback when neither category nor subtype matches.
// Walks the parentId chain back to a hub slug.
const HUB_TO_CARD = {
  egg_hub:     "build",
  chicken_hub: "build",
  beef_hub:    "build",
  pork_hub:    "build",
  turkey_hub:  "build",
  fish_hub:    "build",
  cheese_hub:  "dairy",
  milk_hub:    "dairy",
  yogurt_hub:  "dairy",
};

// Tokens in the ingredient NAME that flip a borderline classification.
// Last-resort tiebreaker when category/subtype/hub all whiff.
const NAME_HINT = [
  [/(milk|cheese|yogurt|butter|cream|kefir)\b/i, "dairy"],
  [/(beef|chicken|pork|turkey|fish|salmon|tuna|sausage|bacon|ham|egg|tofu|tempeh|lentil|bean|chickpea|nut)\b/i, "build"],
  [/(broccoli|spinach|kale|carrot|tomato|cucumber|pepper|onion|garlic|lettuce|cabbage|squash|zucchini|berry|apple|banana|orange|grape|melon|pear|plum|peach)\b/i, "super"],
  [/(rice|pasta|bread|tortilla|noodle|oat|quinoa|flour|cereal|cracker)\b/i, "boost"],
  [/(cookie|cake|candy|chocolate|ice cream|sugar|syrup|honey|dessert)\b/i, "spark"],
  [/(water|juice|smoothie)\b/i, "water"],
];

// Pull the matching card kind for a single ingredient meta object.
// Returns null if nothing maps (e.g. salt, vinegar, oil — those are
// flavor-only and shouldn't surface as a card to a kid).
export function cardKindFromIngredient(meta) {
  if (!meta) return null;
  if (meta.subtype && SUBTYPE_TO_CARD[meta.subtype] !== undefined) {
    return SUBTYPE_TO_CARD[meta.subtype];
  }
  if (meta.category && CATEGORY_TO_CARD[meta.category]) {
    return CATEGORY_TO_CARD[meta.category];
  }
  if (meta.parentId && HUB_TO_CARD[meta.parentId]) {
    return HUB_TO_CARD[meta.parentId];
  }
  if (meta.name) {
    for (const [re, kind] of NAME_HINT) {
      if (re.test(meta.name)) return kind;
    }
  }
  return null;
}

// How many cards does this one recipe line emit? Default 1 per line.
// For count-based lines (eggs, slices, links, patties) parse the
// leading integer so "2 large eggs" → 2 cards, "3 sausages" → 3.
// Weight / volume lines stay at 1 — we don't try to convert grams
// of cheese to slice-counts; one cheese line = one Protect.
function countForLine(line) {
  const text = (line?.amount || line?.text || line?.item || "").toString();
  if (!text) return 1;
  // "2", "2 large", "two", etc. Default to 1 when nothing parses.
  const numMatch = text.match(/^\s*(\d+(?:\.\d+)?)/);
  if (numMatch) {
    const n = parseFloat(numMatch[1]);
    if (Number.isFinite(n) && n >= 1) {
      // Cap at 5 per line so "12 eggs" doesn't dominate the preview.
      return Math.min(Math.round(n), 5);
    }
  }
  const word = text.toLowerCase().trim();
  const WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8 };
  for (const [w, v] of Object.entries(WORDS)) {
    if (word.startsWith(w + " ") || word === w) return v;
  }
  return 1;
}

// cardsForRecipe — sum per-line predictions. Caller passes a
// `getIngredient(id)` to resolve canonical ids to ingredient meta;
// `recipe.ingredients[]` is expected to be the same shape stored in
// user_recipes / dream-mode generations. Falls back to
// canonical_id, canonicalId, or text-only matching depending on
// shape.
//
// Returns `{ build, super, boost, spark, dairy, water }` — each
// capped at 5 so a multi-protein recipe doesn't dump 8 Builds.
export function cardsForRecipe(recipe, { getIngredient } = {}) {
  const out = { build: 0, super: 0, boost: 0, spark: 0, dairy: 0, water: 0 };
  const lines = recipe?.ingredients || recipe?.items || [];
  for (const line of lines) {
    const id = line?.canonical_id || line?.canonicalId || line?.id;
    const meta = id && getIngredient ? getIngredient(id) : null;
    // Pull a name from the line for the NAME_HINT fallback if no
    // canonical resolves — the AI emits free text in some flows.
    const fallback = { name: line?.item || line?.name || line?.text || "" };
    const kind = cardKindFromIngredient(meta || fallback);
    if (!kind) continue;
    out[kind] += countForLine(line);
  }
  for (const k of Object.keys(out)) {
    out[k] = Math.min(out[k], 5);
  }
  return out;
}

// Same predictor but for a single kitchen-item row (pantry_items
// shape). Returns the card kind + a count of 1 — kitchen items are
// surfaced as request candidates, not as multi-ingredient meals, so
// they map to a single card. The kid's "snack" flow.
export function cardForKitchenItem(item, { getIngredient } = {}) {
  if (!item) return { kind: null, count: 1 };
  const id = item.canonical_id || item.canonicalId;
  const meta = id && getIngredient ? getIngredient(id) : null;
  const fallback = { name: item.name || "" };
  const kind = cardKindFromIngredient(meta || {
    name: fallback.name,
    category: item.category,
    subtype: item.subtype,
  });
  return { kind, count: 1 };
}

// Total count across the prediction (used to render "0 cards" empty
// state vs the chips).
export function totalCardCount(prediction) {
  if (!prediction) return 0;
  return Object.values(prediction).reduce((a, b) => a + (b || 0), 0);
}
